
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import SpinnerLoader from '@/components/SpinnerLoader.vue'
import api from '@/api/api'
import { useProject } from '@/hooks/useProject'
import { useBillingStore } from '@/store/stores/billing'
import { tariffsFeatures } from '@/modules/marketplace/billing.consts'
import {
    getEffectiveConnectors,
    getProjectChannelTableDisplay,
} from '@/helpers/channels/channelTableDisplay'
import { formatInternationalPhone } from '@/helpers/formatters/phoneFormatter'
import store from '@/store'

type ChannelOption = {
    slotId: string
    name: string
    phone: string
    empty: boolean
    avatar: {
        color: string
        symbol: string
        image: string | null
        name: string
    }
}

type ComparisonRow = {
    name: string
    current: string
    after: string
    lost: boolean
    icon: string
}

const CHANNELS_ICON =
    '<svg viewBox="0 0 24 24" fill="none"><path d="M4 4h16v16H4zM8 2v4m8-4v4M2 8h20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
const ACTIVATIONS_ICON =
    '<svg viewBox="0 0 24 24" fill="none"><path d="m13 2-9 12h7l-1 8 9-12h-7l1-8Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
const MULTICHAT_ICON =
    '<svg viewBox="0 0 24 24" fill="none"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
const ANALYTICS_ICON =
    '<svg viewBox="0 0 24 24" fill="none"><path d="M4 19V9m6 10V5m6 14v-7m4 7H2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
const FEATURES_ICON =
    '<svg viewBox="0 0 24 24" fill="none"><path d="M3 9h18M5 9l1 11h12l1-11M9 9V5a3 3 0 0 1 6 0v4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'

const flattenFeatures = () =>
    tariffsFeatures.flatMap(group => group.children || [])

const findFeature = (name: string) => flattenFeatures().find(item => item.name === name)

const formatDateLabel = (value?: string | Date | null) => {
    if (!value) return 'конца текущего периода'
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) return 'конца текущего периода'
    return date.toLocaleDateString('ru-RU')
}

const addOneDay = (value?: string | null) => {
    if (!value) return null
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return null
    date.setDate(date.getDate() + 1)
    return date
}

const pluralChannels = (count: number) => {
    const abs = Math.abs(count) % 100
    const last = abs % 10
    if (abs > 10 && abs < 20) return 'рабочих каналов'
    if (last === 1) return 'рабочий канал'
    if (last >= 2 && last <= 4) return 'рабочих канала'
    return 'рабочих каналов'
}

const formatChannelCount = (count: number) => `${count} ${pluralChannels(count)}`

const getTariffFeatureId = (tariff: { id?: string; integrationId?: string; baseTariffId?: string | null } | null) =>
    String(tariff?.baseTariffId || tariff?.id || tariff?.integrationId || '')

const getNumericFeatureValue = (
    featureName: string,
    tariff: { id?: string; integrationId?: string; baseTariffId?: string | null; extra?: Record<string, unknown> | null } | null
) => {
    const feature = findFeature(featureName) as {
        value?: Record<string, string>
        valueFromExtra?: string
    } | undefined
    if (!feature || !tariff) return ''
    if (feature.valueFromExtra) {
        const extraValue = tariff.extra?.[feature.valueFromExtra]
        if (extraValue !== undefined && extraValue !== null && extraValue !== '') {
            return String(extraValue)
        }
    }
    return feature.value?.[getTariffFeatureId(tariff)] || ''
}

const isFeatureIncluded = (
    featureName: string,
    tariff: { id?: string; integrationId?: string; baseTariffId?: string | null } | null
) => {
    const feature = findFeature(featureName) as { inTariffs?: string[] } | undefined
    if (!feature?.inTariffs) return false
    return feature.inTariffs.includes(getTariffFeatureId(tariff))
}

export default defineComponent({
    name: 'DowngradeTariffModal',
    components: { SpinnerLoader },
    props: {
        opened: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        success: {
            type: Boolean,
            default: false,
        },
        skipWizard: {
            type: Boolean,
            default: false,
        },
        paymentUrl: {
            type: String,
            default: '',
        },
    },
    emits: ['cancel', 'stay', 'confirm', 'close-success', 'pay-now'],
    setup(props, { emit }) {
        const { project } = useProject()
        const billingStore = useBillingStore()
        const titleId = 'downgrade-tariff-modal-title'
        const step = ref(1)
        const selectedSlotIds = ref<string[]>([])
        const confirmedChecks = ref<boolean[]>([])
        const channels = ref<ChannelOption[]>([])
        const channelsLoading = ref(false)
        const mustChoose = ref(false)
        const notAffectedNames = ref('')
        const apiTargetCount = ref(0)

        const currentTariff = computed(() => billingStore.getCurrentTariff(project.value?.id))
        const selectedTariff = computed(() =>
            billingStore.selectedTariffId ? billingStore.getTariffById(billingStore.selectedTariffId) : null
        )
        const currentTariffName = computed(() => currentTariff.value?.name || 'текущем тарифе')
        const newTariffName = computed(() => selectedTariff.value?.name || 'новом тарифе')
        const currentEndDate = computed(
            () => currentTariff.value?.endOfUse || currentTariff.value?.activeUntil || ''
        )
        const currentEndLabel = computed(() => formatDateLabel(currentEndDate.value))
        const changeDateLabel = computed(() => formatDateLabel(addOneDay(currentEndDate.value)))
        const keepCount = computed(() => {
            const extraSlots = Number(selectedTariff.value?.extra?.channelSlots)
            if (Number.isFinite(extraSlots) && extraSlots > 0) return extraSlots
            const featureValue = Number(
                getNumericFeatureValue('Количество каналов отправки сообщений', selectedTariff.value)
            )
            if (Number.isFinite(featureValue) && featureValue > 0) return featureValue
            return 1
        })
        const currentChannelCount = computed(() => {
            const extraSlots = Number(currentTariff.value?.extra?.channelSlots)
            if (Number.isFinite(extraSlots) && extraSlots > 0) return extraSlots
            const featureValue = Number(
                getNumericFeatureValue('Количество каналов отправки сообщений', currentTariff.value)
            )
            if (Number.isFinite(featureValue) && featureValue > 0) return featureValue
            return 0
        })
        const safeTargetCount = (value: unknown, fallback = 0) => {
            const count = Math.floor(Number(value))
            return Number.isFinite(count) && count >= 0 ? count : fallback
        }
        const selectionLimit = computed(() => {
            const fromApi = safeTargetCount(apiTargetCount.value)
            if (fromApi > 0) return fromApi
            return keepCount.value > 0 ? keepCount.value : 1
        })
        const isSingleSlotChoice = computed(() => selectionLimit.value <= 1)
        const needsChannelStep = computed(() => mustChoose.value && channels.value.length > 0)
        const stepItems = computed(() => {
            const items = [{ id: 1, label: 'Последствия' }]
            if (needsChannelStep.value) items.push({ id: 2, label: 'Канал' })
            items.push({ id: 3, label: 'Подтверждение' })
            return items.map((item, index) => ({ ...item, index: index + 1 }))
        })
        const busy = computed(() => props.loading || (props.opened && channelsLoading.value && !isSuccessView.value))
        const isSuccessView = computed(() => props.success || step.value === 4)
        const showWizard = computed(() => (
            !isSuccessView.value
            && (!props.skipWizard || (mustChoose.value && channels.value.length > 0))
        ))
        const showStepper = computed(() => showWizard.value)
        const hasPaymentUrl = computed(() => Boolean(props.paymentUrl?.trim()))
        const comparisonRows = computed<ComparisonRow[]>(() => {
            const currentChannels = currentChannelCount.value
            const nextChannels = keepCount.value
            const currentActivations = getNumericFeatureValue('Количество активаций', currentTariff.value)
            const nextActivations = getNumericFeatureValue('Количество активаций', selectedTariff.value)
            const currentMultichat = isFeatureIncluded('Мультичат', currentTariff.value)
            const nextMultichat = isFeatureIncluded('Мультичат', selectedTariff.value)
            const currentAnalytics = isFeatureIncluded('Аналитика', currentTariff.value)
            const nextAnalytics = isFeatureIncluded('Аналитика', selectedTariff.value)
            const formatAvailability = (included: boolean) => (included ? 'Доступен' : 'Недоступен')
            const formatActivations = (value: string) => {
                if (!value) return '—'
                if (value.toLowerCase() === 'безлимит') return '100+'
                return value
            }
            return [
                {
                    name: 'Каналы',
                    current: currentChannels ? formatChannelCount(currentChannels) : 'По тарифу',
                    after: formatChannelCount(nextChannels),
                    lost: nextChannels < currentChannels,
                    icon: CHANNELS_ICON,
                },
                {
                    name: 'Активации в рассылках',
                    current: formatActivations(currentActivations),
                    after: formatActivations(nextActivations),
                    lost: Boolean(currentActivations && nextActivations && currentActivations !== nextActivations),
                    icon: ACTIVATIONS_ICON,
                },
                {
                    name: 'Мультичат',
                    current: formatAvailability(currentMultichat),
                    after: formatAvailability(nextMultichat),
                    lost: currentMultichat && !nextMultichat,
                    icon: MULTICHAT_ICON,
                },
                {
                    name: 'Раздел аналитики',
                    current: currentAnalytics ? 'Полный доступ' : 'Недоступен',
                    after: nextAnalytics ? 'Полный доступ' : 'Недоступен',
                    lost: currentAnalytics && !nextAnalytics,
                    icon: ANALYTICS_ICON,
                },
                {
                    name: 'Новые функции',
                    current: 'По условиям тарифа',
                    after: 'Отдельно в Маркетплейсе',
                    lost: true,
                    icon: FEATURES_ICON,
                },
            ]
        })
        const title = computed(() => {
            if (isSuccessView.value) return 'Переход запланирован'
            if (props.skipWizard) return 'Планируем изменение тарифа'
            if (step.value === 1) return 'При переходе часть возможностей отключится'
            if (step.value === 2) return 'Выберите канал, который останется'
            if (step.value === 3) return `Подтвердите переход на тариф «${newTariffName.value}»`
            return 'Переход запланирован'
        })
        const subtitle = computed(() => {
            if (isSuccessView.value) return 'Изменение тарифа'
            if (props.skipWizard) return 'Отправляем запрос'
            if (step.value === 1) return `Переход на тариф «${newTariffName.value}»`
            if (step.value === 2) {
                return `В тариф «${newTariffName.value}» входит ${formatChannelCount(keepCount.value)}`
            }
            if (step.value === 3) return `Изменение применится ${changeDateLabel.value}`
            return 'Изменение тарифа'
        })
        const channelNoticeTitle = computed(
            () => `В тарифе «${newTariffName.value}» останется ${formatChannelCount(selectionLimit.value)},`
        )
        const channelNoticeText = computed(
            () =>
                `поэтому выберите, ${selectionLimit.value > 1 ? 'какие' : 'какой'} из мессенджеров оставить. Другие каналы перестанут работать с ${changeDateLabel.value}. Больше каналов можно будет купить дополнительно к тарифу.`
        )
        const selectedChannels = computed(() =>
            channels.value.filter(channel => selectedSlotIds.value.includes(channel.slotId))
        )
        const selectedChannelNames = computed(() =>
            selectedChannels.value.map(channel => channel.name).join(', ')
        )
        const removedChannelNames = computed(() =>
            channels.value
                .filter(channel => !channel.empty && !selectedSlotIds.value.includes(channel.slotId))
                .map(channel => channel.name)
                .join(', ')
        )
        const confirmationChecks = computed(() => {
            const checks: string[] = []
            if (keepCount.value < currentChannelCount.value) {
                checks.push(
                    `Я понимаю, что после перехода останется только ${formatChannelCount(keepCount.value)}.`
                )
            }
            if (removedChannelNames.value) {
                checks.push('Я понимаю, что невыбранные каналы будут удалены.')
            }
            const lostNames = comparisonRows.value
                .filter(row => row.lost && (row.name === 'Мультичат' || row.name === 'Раздел аналитики'))
                .map(row => (row.name === 'Раздел аналитики' ? 'раздел аналитики' : row.name))
            if (lostNames.length) {
                checks.push(`Я понимаю, что ${lostNames.join(' и ')} станут недоступны.`)
            }
            checks.push('Я понимаю, что перерасчёт текущей подписки не производится.')
            return checks
        })
        const allChecksConfirmed = computed(
            () =>
                confirmationChecks.value.length > 0 &&
                confirmationChecks.value.every((_, index) => confirmedChecks.value[index] === true)
        )
        const canGoNext = computed(() => {
            if (channelsLoading.value && (step.value === 1 || step.value === 2)) return false
            if (step.value === 2) {
                if (!channels.value.length) return true
                const limit = selectionLimit.value
                if (limit <= 1) return selectedSlotIds.value.length === 1
                return selectedSlotIds.value.length >= 1 && selectedSlotIds.value.length <= limit
            }
            if (step.value === 3) return allChecksConfirmed.value
            return true
        })
        const nextButtonLabel = computed(() => (step.value === 3 ? 'Перейти к оплате' : 'Продолжить'))
        const nextButtonClass = computed(() => {
            if (step.value === 1) return 'downgrade-modal__btn--secondary'
            if (step.value === 3) return 'downgrade-modal__btn--danger'
            return 'downgrade-modal__btn--primary'
        })

        const slotIdOf = (slot: any) => String(slot?.slotId || slot?.id || '')
        const slotsOf = (slotType: any) => (Array.isArray(slotType?.slots) ? slotType.slots : [])
        const mapSlot = (slot: any): ChannelOption | null => {
            try {
                const slotId = slotIdOf(slot)
                if (!slotId) return null
                const connectors = getEffectiveConnectors((project.value?.connectors || []) as any[])
                const display = getProjectChannelTableDisplay(slot?.channel ?? null, {
                    effectiveConnectors: connectors,
                    connectorsAvatars: store.state.connectorsAvatars || [],
                    isChannelsRework: Boolean(project.value?.isChannelsRework),
                })
                return {
                    slotId,
                    name: display.name,
                    phone: display.empty
                        ? 'Пустой слот'
                        : (display.phone ? formatInternationalPhone(display.phone) : ''),
                    empty: display.empty,
                    avatar: display.avatar,
                }
            } catch (_) {
                return null
            }
        }
        const defaultSelectedSlotIds = (items: ChannelOption[], limit: number) => {
            try {
                if (!items.length) return [] as string[]
                const take = Math.max(0, limit)
                const sorted = [...items].sort((left, right) => Number(left.empty) - Number(right.empty))
                return sorted.slice(0, take).map(item => item.slotId).filter(Boolean)
            } catch (_) {
                return []
            }
        }
        const loadChannels = async () => {
            const projectId = project.value?.id
            const integrationId = String(
                selectedTariff.value?.id
                || billingStore.selectedTariffId
                || ''
            )
            if (!projectId || !integrationId) {
                channels.value = []
                mustChoose.value = false
                notAffectedNames.value = ''
                selectedSlotIds.value = []
                apiTargetCount.value = 0
                return
            }
            channelsLoading.value = true
            try {
                const { data, error } = await api.marketplace.getDowngradeChannelSelection({
                    projectId,
                    integrationId,
                })
                if (error || !data) {
                    channels.value = []
                    mustChoose.value = false
                    notAffectedNames.value = ''
                    selectedSlotIds.value = []
                    apiTargetCount.value = 0
                    return
                }
                const slotTypes = Array.isArray(data.slotTypes) ? data.slotTypes : []
                mustChoose.value = slotTypes.some((item: any) => item?.mustChoose === true)
                const pickerTypes = slotTypes.filter((item: any) => item?.mustChoose === true)
                apiTargetCount.value = pickerTypes.reduce(
                    (sum: number, item: any) => sum + safeTargetCount(item?.targetCount),
                    0
                )
                const mapped = pickerTypes.flatMap((item: any) => (
                    slotsOf(item).map(mapSlot).filter((slot: ChannelOption | null): slot is ChannelOption => Boolean(slot?.slotId))
                ))
                channels.value = mapped
                selectedSlotIds.value = defaultSelectedSlotIds(mapped, selectionLimit.value)
                notAffectedNames.value = (Array.isArray(data.notAffected) ? data.notAffected : [])
                    .map((channel: any) => channel?.name || channel?.title || channel?.type || '')
                    .filter(Boolean)
                    .join(', ')
            } catch (_) {
                channels.value = []
                mustChoose.value = false
                notAffectedNames.value = ''
                selectedSlotIds.value = []
                apiTargetCount.value = 0
            } finally {
                channelsLoading.value = false
            }
        }
        const resetState = () => {
            step.value = 1
            selectedSlotIds.value = []
            confirmedChecks.value = []
            channels.value = []
            mustChoose.value = false
            notAffectedNames.value = ''
            apiTargetCount.value = 0
        }
        const isSlotSelected = (slotId: string) => selectedSlotIds.value.includes(slotId)
        const toggleSlot = (slotId: string, event?: Event) => {
            try {
                if (!slotId) return
                const limit = selectionLimit.value
                if (limit <= 1) {
                    if (isSlotSelected(slotId) && channels.value.length) return
                    selectedSlotIds.value = [slotId]
                    return
                }
                if (isSlotSelected(slotId)) {
                    if (selectedSlotIds.value.length <= 1 && channels.value.length) return
                    selectedSlotIds.value = selectedSlotIds.value.filter(id => id !== slotId)
                    return
                }
                if (selectedSlotIds.value.length >= limit) return
                selectedSlotIds.value = [...selectedSlotIds.value, slotId]
            } catch (_) {
            } finally {
                // Галочку браузер переключает ещё до обработчика. Если выбор не изменился (последний канал
                // не снимается, сверх лимита не отмечается), Vue не перерисует `checked` — вернём её сами.
                const input = event?.target as HTMLInputElement | undefined
                if (input) input.checked = isSlotSelected(slotId)
            }
        }
        const slotHint = (slotId: string) => {
            const slot = channels.value.find(item => item.slotId === slotId)
            if (isSlotSelected(slotId)) {
                return slot?.empty
                    ? `Пустой слот останется в тарифе «${newTariffName.value}»`
                    : `Останется в тарифе «${newTariffName.value}»`
            }
            return slot?.empty
                ? 'Слот не войдёт в новый тариф'
                : `Будет удалён ${changeDateLabel.value}`
        }
        const handleClose = () => {
            if (props.loading) return
            if (props.success || step.value === 4) {
                emit('close-success')
                return
            }
            emit('cancel')
        }
        const handleStay = () => {
            if (props.loading) return
            emit('stay')
        }
        const handlePayLater = () => {
            if (props.loading) return
            emit('close-success')
        }
        const handlePayNow = () => {
            if (props.loading) return
            emit('pay-now')
        }
        const goBack = () => {
            if (step.value === 3 && !needsChannelStep.value) {
                step.value = 1
                return
            }
            step.value = Math.max(1, step.value - 1)
        }
        const goNext = () => {
            if (!canGoNext.value || busy.value) return
            if (step.value === 1) {
                step.value = needsChannelStep.value ? 2 : 3
                return
            }
            if (step.value === 2) {
                step.value = 3
                return
            }
            emit('confirm', selectedSlotIds.value)
        }
        const handleKeydown = (event: KeyboardEvent) => {
            if (!props.opened || event.key !== 'Escape') return
            handleClose()
        }

        watch(
            () => props.opened,
            async opened => {
                if (!opened) {
                    resetState()
                    return
                }
                resetState()
                try {
                    await loadChannels()
                    if (!props.opened) return
                    if (props.skipWizard && !mustChoose.value) {
                        emit('confirm', selectedSlotIds.value)
                        return
                    }
                    if (props.skipWizard && mustChoose.value) {
                        step.value = 2
                    }
                } catch (_) {
                    if (!props.opened) return
                    if (props.skipWizard) emit('confirm', [])
                }
            }
        )
        watch(
            () => props.success,
            success => {
                if (success && props.opened) step.value = 4
            }
        )
        watch(
            confirmationChecks,
            checks => {
                if (confirmedChecks.value.length === checks.length) return
                confirmedChecks.value = checks.map((_, index) => confirmedChecks.value[index] === true)
            },
            { immediate: true }
        )

        onMounted(() => {
            document.addEventListener('keydown', handleKeydown)
        })
        onBeforeUnmount(() => {
            document.removeEventListener('keydown', handleKeydown)
        })

        return {
            titleId,
            step,
            stepItems,
            busy,
            showWizard,
            showStepper,
            isSuccessView,
            hasPaymentUrl,
            title,
            subtitle,
            currentTariffName,
            newTariffName,
            currentEndLabel,
            changeDateLabel,
            keepCount,
            comparisonRows,
            channels,
            channelsLoading,
            channelNoticeTitle,
            channelNoticeText,
            confirmationChecks,
            confirmedChecks,
            selectedChannelNames,
            isSingleSlotChoice,
            notAffectedNames,
            removedChannelNames,
            canGoNext,
            nextButtonLabel,
            nextButtonClass,
            isSlotSelected,
            toggleSlot,
            slotHint,
            handleClose,
            handleStay,
            handlePayLater,
            handlePayNow,
            goBack,
            goNext,
        }
    },
})
